import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { GeneralService } from 'src/app/services/general.service';
import { MarketInfoService } from 'src/app/services/marketInfo.service';
import { IMarketLogo, IMarketSettings } from 'src/app/Shared/Interfaces/IMarketSettings';
import { ANGULAR_MODULES, GENERAL_COMPONENTS, Operational_COMPONENTS } from 'src/app/Shared/shared-imports';
import Swal from 'sweetalert2';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { WordReplacePipe } from 'src/app/Shared/word-replace.pipe';
import { WordReplacementService } from 'src/app/services/word-replacement.service';
import { UserPermissionHandlerDirective } from 'src/app/Shared/userPermissionHandler.directive';
import { TooltipModule } from '@syncfusion/ej2-angular-popups';
import { IUserPermissionHandler } from 'src/app/Shared/model/CommonModel';
import { EActionTypes, ECountrycode, ELovTypes, EUserSecurityFunctionID } from 'src/app/Shared/enum';
import { CommonCalculatorService } from 'src/app/Shared/commonCalculators.service';
import { MaskedTextBoxModule } from '@syncfusion/ej2-angular-inputs';
@Component({
  selector: 'app-ops-market-information',
  standalone: true,
  imports: [Operational_COMPONENTS, ANGULAR_MODULES, GENERAL_COMPONENTS, WordReplacePipe, UserPermissionHandlerDirective, TooltipModule, MaskedTextBoxModule],
  templateUrl: './ops-market-information.html',
  styleUrl: './ops-market-information.scss',
})

export class OpsMarketInformation {

  public loaderIcon: Promise<boolean>;
  isCollapsed = true;
  logoPreviewUrl: string = './assets/images/NoPhoto.svg';
  marketSettingsForm: FormGroup;
  marketTerms: any[] = [];
  vendorTerms: any[] = [];
  boothTerms: any[] = [];
  states: any[] = [];
  countries: any[] = [];
  marketTimeZones: any[] = [];
  private destroy$ = new Subject<void>();
  document: any;
  originalFormValue: any;
  marketLogo: IMarketLogo = {
    MarketId: null,
    MarketLogoName: '',
    UploadedMarketLogoContent: null,
    DeleteFlag: false
  };
  statesAvailable: boolean = true;
  editRight = false;
  public PhoneNumberMask: String = '(000)000-0000';
  viewPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.MarketInformation, Action: EActionTypes.VIEW };
  editPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.MarketInformation, Action: EActionTypes.EDIT };

  constructor(
    private fb: FormBuilder,
    private marketInfoService: MarketInfoService,
    private generalService: GeneralService,
    private toastr: ToastrService,
    private router: Router,
    private sharedHTMLSettings: SharedHTMLSettingsDataService,
    private wordReplacementService: WordReplacementService,
    private commonCalculatorService: CommonCalculatorService
  ) { }

  ngOnInit() {
    this.getUserPermissionForMarketInformation();
    this.initForm();
    this.loadTermsUsed();
    this.loadCountries();
    this.loadMarketTimeZones();
    this.loadMarketSettings();
  }

  getUserPermissionForMarketInformation(): void {
    const userRightsForMarketInformation = this.generalService.getUserPermissionsForModule(EUserSecurityFunctionID.MarketInformation);
    this.editRight = userRightsForMarketInformation.EditRight;
  }

  initForm(): void {
    this.marketSettingsForm = this.fb.group({
      MarketTerm: [null, [Validators.required]],
      VendorTerm: [null, [Validators.required]],
      BoothTerm: [null, [Validators.required]],
      MarketId: [''],
      CorporateName: ['', [Validators.maxLength(50)]],
      MarketName: ['', [Validators.required, Validators.maxLength(100)]],
      MarketCity: ['', [Validators.required, Validators.maxLength(50)]],
      MarketZipCode: ['', [Validators.required, Validators.maxLength(10), Validators.pattern(/^\d{5}(?:-\d{4})?$/)]],
      MarketAddress: ['', [Validators.maxLength(100)]],
      MarketStateCode: [null, [Validators.required]],
      MarketCountryCode: [null, [Validators.required]],
      EmailAddress: ['', [Validators.pattern('^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$'), Validators.maxLength(75)]],
      FacebookId: ['', [Validators.maxLength(40)]],
      TwitterId: ['', [Validators.maxLength(40)]],
      WebAdress: ['', [Validators.maxLength(400), Validators.pattern(/^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/)]],
      TimeZoneId: [null],
      Telephone: [''],
      Fax: ['', [Validators.maxLength(20), Validators.pattern(/^\+?[0-9]{1,3}?[-\s.]?(\(?[0-9]{1,4}\)?[-\s.]?){1,4}$/)]],
      LogoImage: [''],
    });
  }

  loadTermsUsed(): void {
    //load market terms
    this.generalService.getListOfValuesByType(ELovTypes['Market Term']).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.marketTerms = res;
      },
      error: err => console.error('Failed to load market terms', err)
    });
    //load vendor terms
    this.generalService.getListOfValuesByType(ELovTypes['Vendor Term']).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.vendorTerms = res;
      },
      error: err => console.error('Failed to load vendor terms', err)
    });
    //load booth terms
    this.generalService.getListOfValuesByType(ELovTypes['Booth Term']).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.boothTerms = res;
      },
      error: err => console.error('Failed to load booth Terms', err)
    });
  }

  loadStates(countryCode: number): void {
    this.generalService.getStates(countryCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.states = res;
          this.statesAvailable = res && res.length > 0;
        },
        error: (err) => {
          console.error('Failed to load states for country ' + countryCode, err);
          this.states = [];
          this.statesAvailable = false;
          this.marketSettingsForm.patchValue({ MarketStateCode: '' });
        }
      });
  }

  onCountryChange(event: Event): void {
    const selectEl = event.target as HTMLSelectElement;
    const countryCode = Number(selectEl.value);

    if (countryCode) {
      this.loadStates(countryCode);
      this.marketSettingsForm.patchValue({ MarketStateCode: null });
      this.setZipCodeValidator(countryCode);
    }
  }

  loadCountries(): void {
    this.generalService.getCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res && res.length > 0) {
            this.countries = res;
          } else {
            this.countries = [{ Code: ECountrycode.US, Name: 'US' }];
          }
        },
        error: (err) => {
          console.error('Failed to load countries', err);
          this.countries = [{ Code: ECountrycode.US, Name: 'US' }];
        }
      });
  }

  loadMarketTimeZones(): void {
    this.marketInfoService.getMarketTimeZones()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.marketTimeZones = res;
        },
        error: (err) => {
          console.error('Failed to load US States', err);
        }
      });
  }

  loadMarketSettings(): void {
    this.marketInfoService.getMarketSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: IMarketSettings) => {
          this.marketSettingsForm.patchValue(data);
          let countryCode = data.MarketCountryCode;
          if (!countryCode) {
            countryCode = ECountrycode.US;
            this.marketSettingsForm.patchValue({ MarketCountryCode: countryCode });
          }
          this.loadStates(countryCode);
          this.setZipCodeValidator(countryCode);
          //this.logoPreviewUrl = data.LogoImage ? data.LogoImage : './assets/images/NoPhoto.svg';
          this.logoPreviewUrl = data.LogoImage ? `${data.LogoImage}?v=${Date.now()}` : './assets/images/NoPhoto.svg';
          this.originalFormValue = this.marketSettingsForm.getRawValue();
          this.marketSettingsForm.markAsPristine();
        },
        error: (err) => {
          console.error('Failed to load market settings', err);
        }
      });
  }

  getErrorMessage(controlName: string): string {
    const control = this.marketSettingsForm.get(controlName);
    if (!control || !control.errors) return '';

    const formattedName = controlName.replace(/([A-Z])/g, ' $1').trim();

    if (control.errors['required']) {
      return `${formattedName} is required.`;
    }

    if (control.errors['pattern']) {
      switch (controlName) {
        case 'MarketZipCode':
          const countryCode = Number(this.marketSettingsForm.get('MarketCountryCode')?.value);
          if (countryCode === ECountrycode.US) { // US
            return `${formattedName} must be a valid US ZIP code (e.g., 12345 or 12345-6789).`;
          } else if (countryCode === ECountrycode.CA) { // Canada
            return `${formattedName} must be a valid Canadian postal code (e.g., A1A 1A1).`;
          } else {
            return `${formattedName} is not in a valid format.`;
          }

        case 'EmailAddress':
          return `${formattedName} must be a valid email address (e.g., name@example.com).`;
        case 'WebAdress':
          return `${formattedName} must be a valid URL (e.g., https://example.com).`;
        case 'Telephone':
          return `${formattedName} must be a valid phone number (7-20 digits, may include +, spaces, or -).`;
        case 'Fax':
          return `${formattedName} must be a valid fax number.`;
        default:
          return `${formattedName} is not in a valid format.`;
      }
    }

    return `${formattedName} is invalid.`;
  }

  saveMarketSettings(): void {
    this.commonCalculatorService.trimFormGroup(this.marketSettingsForm);
    if (!this.marketSettingsForm.valid) {
      this.marketSettingsForm.markAllAsTouched();

      const requiredFields: string[] = [];
      const otherErrors: string[] = [];

      Object.keys(this.marketSettingsForm.controls).forEach(key => {
        const control = this.marketSettingsForm.get(key);
        if (control && control.errors) {
          if (control.errors['required']) {
            requiredFields.push(key.replace(/([A-Z])/g, ' $1').trim());
          } else {
            otherErrors.push(this.getErrorMessage(key));
          }
        }
      });
      let messages: string[] = [];
      if (requiredFields.length > 0) {
        messages.push(
          `The following fields are required: ${requiredFields.join(', ')}.`
        );
      }
      if (otherErrors.length > 0) {
        messages = [...messages, ...otherErrors];
      }
      if (messages.length > 0) {
        this.toastr.warning(
          $localize`${messages.join('\n')}`,
          $localize`Invalid Form`,
          this.sharedHTMLSettings.ToasterConfig
        );
      }
      return;
    }
    const currentValue = this.marketSettingsForm.getRawValue();
    const isChanged = JSON.stringify(currentValue) !== JSON.stringify(this.originalFormValue);
    if (!isChanged && !this.canSaveLogo) {
      // this.toastr.info($localize`No changes to save.`, $localize`Info`, this.toasterConfig);
      return;
    }

    Swal.fire({
      title: $localize`Confirm Save`,
      text: $localize`Are you sure you want to save the changes?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: $localize`Yes, Save`,
      cancelButtonText: $localize`Cancel`,
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = this.marketSettingsForm.value;
        this.marketInfoService.saveMarketSettings(formData)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: async () => {
              if (this.canSaveLogo) {
                await this.saveMarketLogo(false);
              }
              this.toastr.success($localize`Market Information saved successfully`, $localize`Success`, this.sharedHTMLSettings.ToasterConfig);
              this.marketSettingsForm.markAsPristine();
              this.loadMarketSettings();

              if (this.haveTermsChanged(formData, this.originalFormValue)) {
                this.generalService.getTermsUsedInMarket()
                  .pipe(takeUntil(this.destroy$))
                  .subscribe(res => {
                    this.wordReplacementService.setTermMap({
                      booth: res.BoothTerm,
                      vendor: res.VendorTerm,
                      market: res.MarketTerm
                    });
                  });
              }
            },
            error: (err) => {
              this.toastr.error($localize`Failed to save Market Information`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
              console.error('Failed to save market settings', err);
            }
          });
      }
    });
  }

  haveTermsChanged(current: any, original: any): boolean {
    return current.MarketTerm !== original.MarketTerm ||
      current.VendorTerm !== original.VendorTerm ||
      current.BoothTerm !== original.BoothTerm;
  }

  addLogoClick() {
    const el = document.getElementById('logoFileInput') as HTMLElement;
    if (el) {
      el.click();
    }
  }

  addLogo(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];

      const allowedTypes = ['image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        alert('Only JPG and PNG files are allowed.');
        input.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        if (file.type === 'image/jpeg') {
          // Already JPG → no conversion needed
          const base64Content = (reader.result as string).split(',')[1];
          this.logoPreviewUrl = reader.result as string;
          this.marketLogo = {
            ...this.marketLogo,
            UploadedMarketLogoContent: base64Content,
            MarketLogoName: file.name, // keep original name
            DeleteFlag: false,
          };
          input.value = '';
        } else {
          // Convert PNG → JPG
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d')!;
            canvas.width = img.width;
            canvas.height = img.height;

            ctx.drawImage(img, 0, 0);

            const jpgDataUrl = canvas.toDataURL('image/jpeg', 0.9); // JPG output
            const base64Content = jpgDataUrl.split(',')[1];
            this.logoPreviewUrl = jpgDataUrl;
            this.marketLogo = {
              ...this.marketLogo,
              UploadedMarketLogoContent: base64Content,
              MarketLogoName: file.name.replace(/\.[^/.]+$/, '') + '.jpg', // force .jpg extension
              DeleteFlag: false,
            };
            input.value = '';
          };
          img.src = reader.result as string;
        }
      };
      reader.readAsDataURL(file);
    }
  }

  removeLogo() {
    this.marketLogo = {
      MarketId: this.marketSettingsForm.value.MarketId || null,
      MarketLogoName: '',
      UploadedMarketLogoContent: null,
      DeleteFlag: true
    };
    this.logoPreviewUrl = './assets/images/NoPhoto.svg';
    const fileInput = document.getElementById('logoFileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  saveMarketLogo(showAlerts: boolean = true, skipPreviewReset: boolean = false): Promise<void> {
    return new Promise((resolve, reject) => {
      const payload: IMarketLogo = {
        MarketId: this.marketLogo.MarketId || this.marketSettingsForm.value.MarketId || 0,
        MarketLogoName: this.marketLogo.MarketLogoName || '',
        UploadedMarketLogoContent: this.marketLogo.UploadedMarketLogoContent || null,
        DeleteFlag: this.marketLogo.DeleteFlag || false,
      };

      if (!payload.UploadedMarketLogoContent && !payload.DeleteFlag) {
        console.error("No logo selected or remove action not flagged");
        resolve();
        return;
      }

      const actionText = payload.DeleteFlag ? 'delete' : 'add';
      const performSave = () => {
        this.marketInfoService.saveMarketLogo(payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res) => {
              if (res.LogoUrl) {
                this.marketLogo.DeleteFlag = false;
                // this.logoPreviewUrl = res.LogoUrl;
                this.logoPreviewUrl = `${res.LogoUrl}?v=${Date.now()}`;
                if (showAlerts) {
                  this.toastr.success($localize`Market Logo added successfully`, $localize`Success`, this.sharedHTMLSettings.ToasterConfig);
                }
              } else if (payload.DeleteFlag) {
                this.logoPreviewUrl = './assets/images/NoPhoto.svg';
                if (showAlerts) {
                  this.toastr.success($localize`Market Logo deleted successfully`, $localize`Success`, this.sharedHTMLSettings.ToasterConfig);
                }
              }

              if (!skipPreviewReset) {
                this.marketLogo = {
                  MarketId: this.marketSettingsForm.value.MarketId || null,
                  MarketLogoName: '',
                  UploadedMarketLogoContent: null,
                  DeleteFlag: false
                };
              }

              resolve();
            },
            error: (err) => {
              console.error('Failed to save market logo', err);
              if (showAlerts) {
                this.toastr.error($localize`Failed to save Market Logo`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
              }
              resolve();
            }
          });
      };

      if (!showAlerts) {
        performSave();
        return;
      }

      Swal.fire({
        title: `Are you sure you want to ${actionText} the Market Logo?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `Yes, ${actionText} it`,
        cancelButtonText: 'Cancel',
        reverseButtons: true
      }).then((result) => {
        if (result.isConfirmed) {
          performSave();
        } else {
          resolve();
        }
      });
    });
  }

  cancelChanges(): void {
    const currentValue = this.marketSettingsForm.getRawValue();
    const isChanged = JSON.stringify(currentValue) !== JSON.stringify(this.originalFormValue);

    if (this.editRight && (isChanged || this.canSaveLogo)) {
      Swal.fire({
        title: $localize`Confirm`,
        text: $localize`Unsaved changes will be lost. Are you sure you want to continue?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: $localize`Yes`,
        cancelButtonText: $localize`No`
      }).then((result) => {
        if (result.isConfirmed) {
          this.router.navigate(['/dashboard']);
        }
      });
    } else {
      this.router.navigate(['/dashboard']);
    }
  }

  get canSave(): boolean {
    return this.marketSettingsForm.dirty && this.marketSettingsForm.valid;
  }

  get isDefaultLogo(): boolean {
    return this.logoPreviewUrl === './assets/images/NoPhoto.svg';
  }

  get canSaveLogo(): boolean {
    return !!this.marketLogo.UploadedMarketLogoContent || this.marketLogo.DeleteFlag;
  }

  get canRemoveLogo(): boolean {
    return !this.isDefaultLogo && !this.marketLogo.DeleteFlag;
  }

  get isNoPhoto(): boolean {
    return this.logoPreviewUrl.includes('NoPhoto.svg');
  }

  setZipCodeValidator(countryCode: number) {
    this.commonCalculatorService.setZipCodeValidator(countryCode, this.marketSettingsForm.get('MarketZipCode'));
  }

  allowOnlyFaxChars(event: KeyboardEvent) {
    this.commonCalculatorService.allowOnlyFaxChars(event);
  }

  onBeforeOpen(args: any): void {
    if (!args || !args.target || !args.target.dataset || !args.target.dataset.content) {
      args.cancel = true;
    }
  }

  handleToggle(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}